import { parseCookies } from 'nookies'

import configuration from '~/configuration'
import type { IUserInformation } from '~/core/@types/global'
import {
  SESSION_COOKIE_CHUB_CURRENT_TENANT,
  SESSION_COOKIE_IS_DISABLE_SIDEBAR_ITEM,
  SESSION_ERROR_MESSAGE
} from '~/core/constants/cookies'
import { redirectTo } from '~/lib/next/authUtilities'

/**
 * Normalize URL by decoding URI components for comparison
 * This ensures URLs with encoded special characters can be compared correctly
 */
export function normalizeUrlForComparison(url: string): string {
  try {
    // Split URL into parts and decode each segment
    const parts = url.split('/')
    return parts
      .map((part) => {
        try {
          return decodeURIComponent(part)
        } catch {
          return part
        }
      })
      .join('/')
  } catch {
    return url
  }
}

function isOnCareerHubWelcomePage(resolvedUrl: string, tenantSlug: string): boolean {
  const welcomePath = configuration.path.careerHub.welcome(tenantSlug)
  return normalizeUrlForComparison(resolvedUrl).startsWith(normalizeUrlForComparison(welcomePath))
}

function handlePlanExpiration({
  resolvedUrl,
  tenantSlug,
  planInfo,
  user
}: {
  resolvedUrl: string
  tenantSlug: string
  planInfo?: { isExpired: boolean }
  user: IUserInformation
}) {
  if (!planInfo) {
    return null
  }

  const planErrorPages = [
    configuration.path.errorExpiredPlan,
    configuration.path.settings.plans_expired,
    configuration.path.error406
  ]

  // Plan expired and user is owner
  if (
    planInfo.isExpired &&
    user.ownTenant &&
    !resolvedUrl.startsWith(configuration.path.settings.plans_expired)
  ) {
    return redirectTo({
      redirectPath: configuration.path.settings.plans_expired
    })
  }

  // Plan expired and user is not owner
  if (
    planInfo.isExpired &&
    !user.ownTenant &&
    !resolvedUrl.startsWith(configuration.path.errorExpiredPlan)
  ) {
    return redirectTo({
      redirectPath: configuration.path.errorExpiredPlan
    })
  }

  // Plan not expired but on error pages
  if (planErrorPages.some((url) => resolvedUrl.startsWith(url)) && !planInfo.isExpired) {
    return redirectTo({
      redirectPath: configuration.path.careerHub.jobs(tenantSlug)
    })
  }

  return null
}

/**
 * CareerHub Authentication Flow Handler
 */
export const handleAuthenticationChubFlow = ({
  cookies,
  user,
  tenantSlug,
  resolvedUrl,
  planInfo
}: {
  cookies: Record<string, string>
  user?: IUserInformation
  tenantSlug: string | undefined
  resolvedUrl: string
  planInfo?: { isExpired: boolean }
}) => {
  if (!tenantSlug) {
    return redirectTo({
      redirectPath: configuration.path.error404
    })
  }
  const defaultRedirectPath = !cookies[SESSION_ERROR_MESSAGE]
    ? configuration.path.careerHub.login(tenantSlug)
    : configuration.path.careerHub.register(tenantSlug)
  const currentTenant = cookies[SESSION_COOKIE_CHUB_CURRENT_TENANT]

  const returnUrlIncludeExtension = resolvedUrl?.startsWith(configuration.path.extension.list)

  try {
    const cookies = parseCookies()
    const companyBlocked = cookies[SESSION_COOKIE_IS_DISABLE_SIDEBAR_ITEM] || false
    const tenantCount = user?.tenants?.data?.length || 0
    // Check if user has currentTenant (even if tenantsCookie is empty)
    const hasCurrentTenant = !!(user?.currentTenant && Object.keys(user.currentTenant).length > 0)

    // 1. Unauthenticated user
    // Always redirect to CareerHub login when in CareerHub, never redirect to internal login
    if (!user) {
      return redirectTo({
        returnUrl: resolvedUrl,
        redirectPath: defaultRedirectPath
      })
    }

    // 2. Block company page - allow access
    if (resolvedUrl.startsWith(configuration.path.tenantBlocked)) {
      return {
        props: {
          user
        }
      }
    }

    // 3. No tenants - redirect to onboarding
    // IMPORTANT: If user has currentTenant but tenantCount === 0, it means tenantsCookie
    // might be empty but user still has a tenant. Don't redirect in this case.
    if (
      tenantCount === 0 &&
      !hasCurrentTenant &&
      !resolvedUrl.startsWith(configuration.path.noAssociatedCompany)
    ) {
      return redirectTo({
        returnUrl: returnUrlIncludeExtension ? resolvedUrl : undefined,
        redirectPath: configuration.path.noAssociatedCompany
      })
    }

    // 4. Has tenants but on onboarding page - redirect to default
    if (tenantCount >= 1 && resolvedUrl.startsWith(configuration.path.onboarding)) {
      return redirectTo({
        redirectPath: configuration.path.careerHub.jobs(tenantSlug)
      })
    }

    // 5. Check plan expiration
    const planResult = handlePlanExpiration({
      resolvedUrl,
      tenantSlug,
      planInfo,
      user
    })
    if (planResult) return planResult

    // 6. Handle onboarding flow
    if (!companyBlocked) {
      const isOnWelcome = isOnCareerHubWelcomePage(resolvedUrl, tenantSlug)

      // Onboarding = false: redirect from welcome to jobs
      if (user?.onboarding === false && isOnWelcome) {
        return redirectTo({
          redirectPath: configuration.path.careerHub.jobs(tenantSlug)
        })
      }

      // Onboarding = true: redirect to welcome
      if (user?.onboarding === true && !isOnWelcome && !!currentTenant) {
        return redirectTo({
          returnUrl: returnUrlIncludeExtension ? defaultRedirectPath : undefined,
          redirectPath: configuration.path.careerHub.welcome(tenantSlug)
        })
      }
    }

    // 7. All checks passed - allow page access
    return {
      props: {
        user
      }
    }
  } catch (e) {
    // Error handling - always redirect to CareerHub login when in CareerHub
    return redirectTo({
      returnUrl: resolvedUrl,
      redirectPath: defaultRedirectPath
    })
  }
}
